import {Manhole, Plan, Side} from 'types'
import {getClosureSizes} from './manhole'
import {getVaultSize} from "./vault";
import {ElementSize} from "./elementSize";
import {getCombinedSize} from "./composition";
import {getLidSpecsByPlan} from "./lidSpecs";

export interface LidSize extends ElementSize {
    left: number
    right: number
    front: number
    back: number
    bottom: number
    top: number
    manholeIds: string[]
}

const emptyLidSize = {
    width: 0,
    length: 0,
    height: 0,
    x: 0,
    y: 0,
    bottom: 0,
    left: 0,
    right: 0,
    front: 0,
    back: 0,
    top: 0,
    manholeIds: [],
}

export const lidIsFullyCoveredByOrHasNoManholesAtAll = (plan: Plan): boolean =>
    !plan.lid || lidIsFullyCoveredByManhole(plan)

export const lidIsFullyCoveredByManhole = (plan: Plan): boolean => {
    return (
        !!plan.lid &&
        plan.lid.manholes.length === 1 &&
        plan.lid.manholes[0].x <= 0 &&
        plan.lid.manholes[0].x + plan.lid.manholes[0].width >= plan.length &&
        plan.lid.manholes[0].y <= 0 &&
        plan.lid.manholes[0].y + plan.lid.manholes[0].height >= plan.width
    )
}

export const hasMultiRowManholes = (plan: Plan): boolean => {
    return (
        !!plan.lid &&
        plan.lid.manholes.length > 0 &&
        plan.lid.manholes.some(m =>
            (m.orientation === 'horizontal' ? m.width : m.height) > getClosureSizes(m.type).height
        )
    )
}

export const canHaveMultipleLids = (plan: Plan): boolean =>
    !!plan.lid && !!plan.intermediateCeiling && !(plan.lid.type === 'subfloor')

export const hasMultipleLids = (plan: Plan): boolean =>
    canHaveMultipleLids(plan) && plan.lid.manholes.length > 1

export const getLidSize = (plan: Plan, manholes?: Manhole[]): LidSize => {
    if (!plan.lid) return emptyLidSize
    if (!manholes) {
        manholes = plan.lid.manholes
    }
    const vaultSize = getVaultSize(plan, true)
    const getA15B125D400E600LidSize = () => {
        const getLidWithoutCollarSize = () => {
            if (manholes!.length < 1) {
                return {width: 0, length: 0, height: 0, x: 0, y: 0}
            }
            const manhole = manholes![0]
            const manholeRight = manhole.x + manhole.width
            const manholeBack = manhole.y + manhole.height
            const getWidthOutsideClearMeasure = (side: 'left' | 'right' | 'front' | 'back') => {
                return ((side === 'left' || side === 'right') && manhole.orientation === 'horizontal') ||
                ((side === 'front' || side === 'back') && manhole.orientation === 'vertical')
                    ? plan.lid.type === 'A15'
                        ? 7.75
                        : plan.lid.type === 'B125'
                            ? 7.2
                            : plan.lid.type === 'D400'
                                ? 9.1
                                : plan.lid.type === 'E600'
                                    ? 9.1
                                    : 0
                    : plan.lid.type === 'A15'
                        ? 5.5
                        : plan.lid.type === 'B125'
                            ? 4
                            : plan.lid.type === 'D400'
                                ? 7.1
                                : plan.lid.type === 'E600'
                                    ? 7.1
                                    : 0
            }
            const getSidePosition = (side: Side) => {
                return side === 'left'
                    ? manhole.x - getWidthOutsideClearMeasure(side)
                    : side === 'right'
                        ? manholeRight + getWidthOutsideClearMeasure(side)
                        : side === 'front'
                            ? manhole.y - getWidthOutsideClearMeasure(side)
                            : side === 'back'
                                ? manholeBack + getWidthOutsideClearMeasure(side)
                                : 0
            }
            const getHeight = () => {
                return plan.lid.type === 'A15'
                    ? 17.5 : plan.lid.type === 'B125'
                        ? 12.4 : plan.lid.type === 'D400'
                            ? 14 : plan.lid.type === 'E600'
                                ? 15
                                : 0
            }

            return {
                width: getSidePosition('back') - getSidePosition('front'),
                length: getSidePosition('right') - getSidePosition('left'),
                height: getHeight(),
                x: getSidePosition('left'),
                y: getSidePosition('front'),
            }
        }
        const getLidWithCollarSize = () => {
            const isStandardVault = () => lidIsFullyCoveredByOrHasNoManholesAtAll(plan) || !!plan.intermediateCeiling
            const getHeight = () => {
                return !!plan.lid.firmHeight
                    ? plan.lid.firmHeight
                    : (getLidSpecsByPlan(plan)?.defaultHeightMm ?? 0) / 10
            }
            const height = getHeight()
            const hasNoOpenings = manholes!.length < 1
                && (plan.lid.circularBlockOuts?.length ?? 0) < 1
                && (plan.lid.blockOuts?.length ?? 0) < 1

            if (hasNoOpenings) {
                return {
                    width: vaultSize.width,
                    length: vaultSize.length,
                    height: height,
                    left: vaultSize.left,
                    x: vaultSize.left,
                    front: vaultSize.front,
                    y: vaultSize.front,
                    bottom: 0,
                    top: height,
                }
            }
            const manhole = manholes![0]
            const getMinWidthOutsideManholeClearMeasures = (side: Side) => {
                const getA15B125MinWidthOutsideManholeClearMeasures = () => hasMultiRowManholes(plan)
                    ? 35
                    : 14
                const getD400E600MinWidthOutsideManholeClearDimensions = () => {
                    const is19CmExceptionStandardLid =
                        (length: number, width: number) => !!manhole
                            && ((manhole.orientation === 'vertical' && length >= 100 && length <= 260)
                                || (manhole.orientation === 'horizontal' && width >= 100 && width <= 260))
                    const is19CmExceptionStandardVault =
                        () => is19CmExceptionStandardLid(plan.length, plan.width) && lidIsFullyCoveredByManhole(plan)
                    const is19CmExceptionStandardLidOnIntermediateCeiling =
                        () => !!manhole
                            && !!plan.intermediateCeiling
                            && is19CmExceptionStandardLid(manhole.width, manhole.height)
                    const isSideWithoutRabbet =
                        (side: Side) => ((side === 'left' || side === 'right') && manhole.orientation === 'vertical')
                            || ((side === 'front' || side === 'back') && manhole.orientation === 'horizontal')
                    const getMinCollarWidth = () =>
                        (is19CmExceptionStandardVault() || is19CmExceptionStandardLidOnIntermediateCeiling())
                        && isSideWithoutRabbet(side)
                            ? 19
                            : 20

                    return getMinCollarWidth()
                }

                return plan.lid.type === 'A15' || plan.lid.type === 'B125'
                    ? getA15B125MinWidthOutsideManholeClearMeasures()
                    : plan.lid.type === 'D400' || plan.lid.type === 'E600'
                        ? getD400E600MinWidthOutsideManholeClearDimensions()
                        : 0
            }

            if (isStandardVault()) {
                return {
                    width: manhole.height + 2 * getMinWidthOutsideManholeClearMeasures('front'),
                    length: manhole.width + 2 * getMinWidthOutsideManholeClearMeasures('left'),
                    height: height,
                    x: manhole.x - getMinWidthOutsideManholeClearMeasures('left'),
                    y: manhole.y - getMinWidthOutsideManholeClearMeasures('front'),
                }
            }
            const leftMostManholeLeft = manholes!.reduce((acc, m) =>
                Math.min(acc, m.x), Number.POSITIVE_INFINITY)
            const rightMostManholeRight = manholes!.reduce((acc, m) =>
                Math.max(acc, m.x + m.width), Number.NEGATIVE_INFINITY)
            const frontMostManholeFront = manholes!.reduce((acc, m) =>
                Math.min(acc, m.y), Number.POSITIVE_INFINITY)
            const backMostManholeBack = manholes!.reduce((acc, m) =>
                Math.max(acc, m.y + m.height), Number.NEGATIVE_INFINITY)
            const left =
                Math.min(
                    leftMostManholeLeft - getMinWidthOutsideManholeClearMeasures('left'),
                    vaultSize.left
                )
            const right =
                Math.max(
                    rightMostManholeRight + getMinWidthOutsideManholeClearMeasures('right'),
                    vaultSize.right
                )
            const front =
                Math.min(
                    frontMostManholeFront - getMinWidthOutsideManholeClearMeasures('front'),
                    vaultSize.front
                )
            const back =
                Math.max(
                    backMostManholeBack + getMinWidthOutsideManholeClearMeasures('back'),
                    vaultSize.back
                )

            return {
                width: back - front,
                length: right - left,
                height: height,
                x: left,
                y: front,
            }
        }

        return plan.lid.collar === 'without' ? getLidWithoutCollarSize() : getLidWithCollarSize()
    }
    const getSubFloorLidSize = () => {
        if (manholes!.length !== 1) {
            return {
                width: vaultSize.width,
                length: vaultSize.length,
                height: 20,
                left: vaultSize.left,
                x: vaultSize.left,
                front: vaultSize.front,
                y: vaultSize.front,
                bottom: 0,
                top: vaultSize.height,
            }
        }
        const manhole = plan.lid.manholes[0]
        const manholeRight = manhole.x + manhole.width
        const manholeBack = manhole.y + manhole.height
        const left =
            manhole.orientation === 'vertical'
                ? manhole.x <= vaultSize.clearMeasureLeft || !!plan.intermediateCeiling
                    ? manhole.x
                    : vaultSize.left
                : lidIsFullyCoveredByManhole(plan) || !!plan.intermediateCeiling
                    ? manhole.x
                    : Math.min(manhole.x, vaultSize.left)
        const right =
            manhole.orientation === 'vertical'
                ? manholeRight >= vaultSize.clearMeasureRight || !!plan.intermediateCeiling
                    ? manholeRight
                    : vaultSize.right
                : lidIsFullyCoveredByManhole(plan) || !!plan.intermediateCeiling
                    ? manholeRight
                    : Math.max(manholeRight, vaultSize.right)
        const front =
            manhole.orientation === 'horizontal'
                ? manhole.y <= vaultSize.clearMeasureFront || !!plan.intermediateCeiling
                    ? manhole.y
                    : vaultSize.front
                : lidIsFullyCoveredByManhole(plan) || !!plan.intermediateCeiling
                    ? manhole.y
                    : Math.min(manhole.y, vaultSize.front)
        const back =
            manhole.orientation === 'horizontal'
                ? manholeBack >= vaultSize.clearMeasureBack || !!plan.intermediateCeiling
                    ? manholeBack
                    : vaultSize.back
                : lidIsFullyCoveredByManhole(plan) || !!plan.intermediateCeiling
                    ? manholeBack
                    : Math.max(manholeBack, vaultSize.back)
        const thickness = (() => {
            const sizes = getClosureSizes(manhole.type)
            const bracketHeight = sizes.bracketHeight ?? 0
            const bracketDiameter = sizes.bracketDiameter ?? 0
            return sizes.thickness + bracketHeight + bracketDiameter / 2
        })()

        return {
            length: right - left,
            width: back - front,
            height: thickness,
            x: left,
            y: front,
        }
    }

    const size =
        plan.lid.type === 'A15'
        || plan.lid.type === 'B125'
        || plan.lid.type === 'D400'
        || plan.lid.type === 'E600'
            ? {...getA15B125D400E600LidSize(), bottom: 0}
            : plan.lid.type === 'subfloor'
                ? {...getSubFloorLidSize(), bottom: 0}
                : (() => {
                    throw new Error(`Unknown lid type: ${plan.lid.type}`)
                })()

    return {
        ...size,
        left: size.x,
        right: size.x + size.length,
        front: size.y,
        back: size.y + size.width,
        bottom: 0,
        top: size.height,
        manholeIds: manholes.map(m => m.id)
    }
}

export const getLidSizes = (plan: Plan): LidSize[] => {
    if (!plan.lid) return []
    if (!!plan.intermediateCeiling && plan.lid.manholes.length > 1) {
        return plan.lid.manholes.map(m => getLidSize(plan, [m]))
    }
    return [getLidSize(plan)]
}

export const getCombinedLidSize = (plan: Plan): LidSize => {
    if (!plan.lid) return emptyLidSize
    const sizes = getLidSizes(plan)
    const getSize = (l: LidSize) => (): ElementSize => l
    const combinedSize = getCombinedSize(sizes.map(getSize), plan)

    return {
        ...combinedSize,
        left: combinedSize.x,
        right: combinedSize.x + combinedSize.length,
        front: combinedSize.y,
        back: combinedSize.y + combinedSize.width,
        bottom: 0,
        top: combinedSize.height,
        manholeIds: plan.lid.manholes.map(m => m.id),
    }
}

export const getHorizontalAnchorWidth = (plan: Plan, manhole: Manhole): number => {
    return (
        plan.lid.type === 'A15'
        || plan.lid.type === 'B125'
        || plan.lid.type === 'D400'
        || plan.lid.type === 'E600'
    )
    && plan.lid.collar === 'without'
    && !!manhole
    && manhole.orientation === 'horizontal'
        ? plan.lid.type === 'A15'
            ? 2
            : plan.lid.type === 'B125'
                ? 3.2
                : 0
        : 0
}
export const getVerticalAnchorWidth = (plan: Plan, manhole: Manhole): number => {
    return (plan.lid.type === 'A15' || plan.lid.type === 'B125' || plan.lid.type === 'D400' || plan.lid.type === 'E600')
    && plan.lid.collar === 'without'
    && !!manhole
    && manhole.orientation === 'vertical'
        ? plan.lid.type === 'A15'
            ? 2
            : plan.lid.type === 'B125'
                ? 3.2
                : 0
        : 0
}

export const getLidTopToBevelTopDistance = (plan: Plan): number => {
    const specs = getLidSpecsByPlan(plan);

    return (specs?.lidTopToBevelTopMm ?? 0) / 10;
}

export const getBevelHeight = (plan: Plan): number => {
    const specs = getLidSpecsByPlan(plan);

    return (specs?.bevelHeightMm ?? 0) / 10;
}

export const getBevelBottom = (plan: Plan): number => {
    const lidSize = getLidSize(plan)

    return lidSize.height - getLidTopToBevelTopDistance(plan) - getBevelHeight(plan)
}
export const getDefaultManholeMaxOverlap = (): number => 4
export const getSubFloorManholeMaxOverlap = (plan: Plan): number => plan.wallWidth
export const getManholeMaxOverlap = (plan: Plan): number => {
    return plan.lid.type === 'subfloor' ? getSubFloorManholeMaxOverlap(plan) : getDefaultManholeMaxOverlap()
}
export const getManholeMaxOverlaps = (plan: Plan, manhole: Manhole) => {
    return plan.lid.type === 'subfloor'
        ? manhole.orientation === 'vertical'
            ? {
                left: getSubFloorManholeMaxOverlap(plan),
                right: getSubFloorManholeMaxOverlap(plan),
                front: Number.POSITIVE_INFINITY,
                back: Number.POSITIVE_INFINITY
            }
            : {
                left: Number.POSITIVE_INFINITY,
                right: Number.POSITIVE_INFINITY,
                front: getSubFloorManholeMaxOverlap(plan),
                back: getSubFloorManholeMaxOverlap(plan)
            }
        : {
            left: getDefaultManholeMaxOverlap(),
            right: getDefaultManholeMaxOverlap(),
            front: getDefaultManholeMaxOverlap(),
            back: getDefaultManholeMaxOverlap()
        }
}
export const getSubFloorManholeMinOverlap = (): number => 10
